import {
  IncidentDocumentation,
  IncidentNotification,
  IncidentType,
  NotificationParty,
  Province,
  ReasonableGroundsDecision
} from "./types";
import { hasText } from "./text";

export const FORM_SECTIONS = ["respond", "assess", "notify", "document", "review"] as const;

export type FormSectionId = (typeof FORM_SECTIONS)[number];

export interface SectionProgress {
  id: FormSectionId;
  required: number;
  done: number;
  remaining: number;
  complete: boolean;
  missing: string[];
}

export interface DocumentationProgressInput {
  type: IncidentType;
  province: Province;
  documentation: IncidentDocumentation;
  notifications: IncidentNotification[];
  reasonable_grounds_decision: ReasonableGroundsDecision;
}

interface Check {
  id: string;
  done: boolean;
}

function filled(value: unknown): boolean {
  return hasText(value);
}

function push(items: Check[], id: string, done: boolean): void {
  items.push({ id, done });
}

function notificationDone(notifications: IncidentNotification[], party: NotificationParty): boolean {
  return notifications.some(
    (row) => row.party === party && row.done && Boolean(row.at) && hasText(row.person_name)
  );
}

/** Parties the nurse must record before finalize. Other rows are reminders. */
export function requiredNotificationParties(input: DocumentationProgressInput): NotificationParty[] {
  const parties: NotificationParty[] = ["charge_nurse"];
  const type = input.type;
  const doc = input.documentation ?? {};
  const decision = input.reasonable_grounds_decision?.decision;
  if (type === "fall") parties.push("physician", "sdm_family");
  if (type === "unsafe_interaction") {
    parties.push("doc");
    if (doc.sdm_responsible !== "yes") parties.push("sdm_family");
  }
  if (type === "wandering_zone") parties.push("sdm_family");
  if (decision === "yes" && doc.criminal_offence === "yes") parties.push("police");
  return parties;
}

function tally(id: FormSectionId, items: Check[]): SectionProgress {
  const missing = items.filter((item) => !item.done).map((item) => item.id);
  return {
    id,
    required: items.length,
    done: items.length - missing.length,
    remaining: missing.length,
    complete: missing.length === 0,
    missing
  };
}

/**
 * Required fields in nursing order. NeuraVue confirmations count in Respond,
 * because that is where the nurse confirms the pre-filled summary.
 */
export function documentationSections(input: DocumentationProgressInput): SectionProgress[] {
  const doc = input.documentation ?? {};
  const type = input.type;
  const decision = input.reasonable_grounds_decision?.decision;

  const respond: Check[] = [];
  const neuravueConfirmed =
    Boolean(doc.resident_confirmed) &&
    Boolean(doc.event_time_confirmed) &&
    Boolean(doc.discovered_at_confirmed) &&
    Boolean(doc.location_confirmed) &&
    Boolean(doc.responders_confirmed) &&
    Boolean(doc.prefilled_confirmed) &&
    Boolean(doc.critical_incident_category_confirmed) &&
    (type !== "fall" || Boolean(doc.post_fall_instrument_confirmed));
  push(respond, "neuravue_confirmed", neuravueConfirmed);
  push(respond, "witnessed", Boolean(doc.witnessed));
  if (doc.witnessed === "yes") push(respond, "witnessed_by", filled(doc.witnessed_by));
  push(respond, "care_given", filled(doc.care_given));
  if (type === "unsafe_interaction" || type === "responsive_behaviour") {
    push(respond, "safety_measures", filled(doc.safety_measures));
  }
  if (type === "responsive_behaviour") push(respond, "intervention", filled(doc.intervention));
  push(respond, "hospital_transfer", Boolean(doc.hospital_transfer));
  if (doc.hospital_transfer === "yes") push(respond, "hospital_name", filled(doc.hospital_name));

  const assess: Check[] = [];
  if (type === "fall" || type === "unsafe_interaction") {
    push(assess, "consciousness", filled(doc.consciousness));
    push(assess, "pain", filled(doc.pain));
    push(assess, "injuries", Boolean(doc.injuries_none) || filled(doc.injuries));
  }
  if (type === "fall") {
    push(assess, "vitals", filled(doc.vitals));
    push(assess, "post_fall_instrument", filled(doc.post_fall_instrument));
    push(assess, "post_fall_completed_at", Boolean(doc.post_fall_completed_at));
  }
  if (type === "unsafe_interaction" || type === "responsive_behaviour") {
    push(assess, "distress", filled(doc.distress));
  }
  if (type === "responsive_behaviour") push(assess, "triggers", filled(doc.triggers));
  if (type === "wandering_zone") {
    push(assess, "missing_last_seen", Boolean(doc.missing_last_seen));
    push(assess, "missing_found_time", Boolean(doc.missing_found_time));
    push(assess, "missing_found_location", filled(doc.missing_found_location));
  }

  const notify = requiredNotificationParties(input).map((party) => ({
    id: `notify_${party}`,
    done: notificationDone(input.notifications, party)
  }));

  const documentItems: Check[] = [];
  push(documentItems, "observed", filled(doc.observed));
  push(documentItems, "persons_present", filled(doc.persons_present));
  if (type !== "other") push(documentItems, "leading_events", filled(doc.leading_events));
  if (type === "unsafe_interaction") {
    push(documentItems, "other_residents", filled(doc.other_residents));
    push(documentItems, "resident_statement", filled(doc.resident_statement));
    if (decision === "yes") push(documentItems, "investigation_lead", filled(doc.investigation_lead));
  }
  push(documentItems, "immediate_prevention", filled(doc.immediate_prevention));
  push(documentItems, "long_term_actions", filled(doc.long_term_actions));
  if (type === "fall" || type === "responsive_behaviour" || type === "wandering_zone") {
    push(documentItems, "care_plan_updated", filled(doc.care_plan_updated));
  }
  push(documentItems, "outcome", filled(doc.outcome));

  const review: Check[] = [];
  if (decision === "unsure") push(review, "reasonable_grounds", false);
  else push(review, "reasonable_grounds", Boolean(decision));
  if (decision === "yes") push(review, "suspicion", filled(input.reasonable_grounds_decision?.suspicion));
  if (input.province === "QC") {
    push(review, "qc_ah223", Boolean(doc.qc_ah223_confirmed) && Boolean(doc.qc_ah223_required));
  }
  if (doc.late_entry) push(review, "late_entry_reason", filled(doc.late_entry_reason));
  push(review, "attestation_accepted", Boolean(doc.attestation_accepted));
  push(review, "attestation_name", filled(doc.attestation_name));
  push(review, "attestation_designation", filled(doc.attestation_designation));

  return [
    tally("respond", respond),
    tally("assess", assess),
    tally("notify", notify),
    tally("document", documentItems),
    tally("review", review)
  ];
}
